import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getMyCenterFee,
  recordCenterFeePayment,
} from "../../../store/actions/centerActions/centerFee";
import { successAlert, errorAlert } from "../../../utils/alert";

/*
 * Center fees.
 *
 * Two separate things: the registration fee, paid once after the council
 * approves the center, and a yearly renewal. Each is shown only when the
 * council has set an amount for it, and the renewal only opens for payment as
 * its due date comes near, so centers are not asked a year early.
 *
 * This page replaces a dead stub that rendered raw template markup and had no
 * working payment path at all.
 */
const FILE_BASE = "https://nsvec.com/api/public/";

const rupees = (value) => "₹" + Number(value || 0).toLocaleString("en-IN");

const dmy = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("-") : "-";

const Fee = () => {
  const dispatch = useDispatch();
  const [fee, setFee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await dispatch(getMyCenterFee());
    setFee(data || false);
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const openCheckout = async (amount, feeType, label) => {
    if (!amount || amount <= 0) return;
    setPaying(true);

    const loaded = await new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

    if (!loaded) {
      errorAlert("Razorpay could not load. Please check your connection.");
      setPaying(false);
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: Math.round(amount * 100), // paise
      currency: "INR",
      name: process.env.REACT_APP_META_NAME || "NSVEC",
      description: label,
      handler: async (razorpayResponse) => {
        // Only the payment id is sent. The server asks Razorpay what was
        // really paid, so the balance cannot be altered from the browser.
        const form = new FormData();
        form.append("razorpay_payment_id", razorpayResponse.razorpay_payment_id);
        form.append("fee_type", feeType);
        form.append("amount", amount);

        const result = await dispatch(recordCenterFeePayment(form));
        if (result) {
          successAlert(result.message);
          setFee(result.data);
        }
        setPaying(false);
      },
      theme: { color: "#3399cc" },
      modal: { ondismiss: () => setPaying(false) },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", () => {
      errorAlert("Payment failed. Nothing has been charged.");
      setPaying(false);
    });
    rzp.open();
  };

  if (loading) {
    return (
      <div class="mid-content-box">
        <div class="BigTitle">Registration Fee</div>
        <p class="mt-3">Loading...</p>
      </div>
    );
  }

  if (!fee) {
    return (
      <div class="mid-content-box">
        <div class="BigTitle">Registration Fee</div>
        <div class="alert alert-danger mt-3">
          Could not load your fee details. Please try again in a moment.
        </div>
      </div>
    );
  }

  const reg = fee.registration;
  const ren = fee.renewal;
  const regPart = Math.min(Number(reg.partial || 0), Number(reg.balance || 0));
  // Offer the part button only when it is genuinely smaller than the balance.
  const showPart = regPart > 0 && regPart < Number(reg.balance);

  return (
    <div>
      <div class="Title-box">
        <div class="BigTitle">Registration Fee</div>
      </div>

      <div class="mid-content-box">
        {!reg.is_set && (
          <div class="alert alert-warning">
            The registration fee has not been set by the council yet. Please
            check back later.
          </div>
        )}

        {reg.is_set && (
          <>
            <div class="row mb-4">
              <div class="col-md-4">
                <div class="card p-3 mb-2">
                  <small class="text-muted">Total Fee</small>
                  <h4 class="mb-0">{rupees(reg.amount)}</h4>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card p-3 mb-2">
                  <small class="text-muted">Paid</small>
                  <h4 class="mb-0 text-success">{rupees(reg.paid)}</h4>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card p-3 mb-2">
                  <small class="text-muted">Balance</small>
                  <h4 class={reg.balance > 0 ? "mb-0 text-danger" : "mb-0"}>
                    {rupees(reg.balance)}
                  </h4>
                </div>
              </div>
            </div>

            {reg.cleared ? (
              <div class="alert alert-success">
                <strong>Your registration fee is fully paid.</strong> Thank you.
                {Number(fee.overpaid) > 0 && (
                  <div class="mt-1">
                    Our records show {rupees(fee.overpaid)} paid above the fee.
                    Please contact the council about this.
                  </div>
                )}
              </div>
            ) : (
              <div class="mb-4">
                <p>
                  You can pay the whole balance now, or pay a part of it and
                  clear the rest later.
                </p>
                <button
                  class="btn btn-Next mr-2 mb-2"
                  type="button"
                  disabled={paying}
                  onClick={() =>
                    openCheckout(
                      reg.balance,
                      "registration",
                      "Center Registration Fee"
                    )
                  }
                >
                  {Number(reg.paid) > 0 ? "Pay Balance " : "Pay Full "}
                  {rupees(reg.balance)}
                </button>
                {showPart && (
                  <button
                    class="btn btn-comman mb-2"
                    type="button"
                    disabled={paying}
                    onClick={() =>
                      openCheckout(
                        regPart,
                        "registration",
                        "Center Registration Fee (part payment)"
                      )
                    }
                  >
                    Pay Part {rupees(regPart)}
                  </button>
                )}
                {paying && <p class="mt-2">Opening payment window...</p>}
              </div>
            )}
          </>
        )}

        {/* ---- yearly renewal ---- */}
        {ren.is_set && (
          <>
            <div class="BigTitle mt-4">Yearly Renewal</div>
            <div class="row mb-3 mt-2">
              <div class="col-md-4">
                <div class="card p-3 mb-2">
                  <small class="text-muted">Renewal Fee</small>
                  <h4 class="mb-0">{rupees(ren.amount)}</h4>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card p-3 mb-2">
                  <small class="text-muted">Renewal Due On</small>
                  <h4 class={ren.overdue ? "mb-0 text-danger" : "mb-0"}>
                    {dmy(ren.due_date)}
                  </h4>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card p-3 mb-2">
                  <small class="text-muted">Balance</small>
                  <h4 class={ren.balance > 0 ? "mb-0 text-danger" : "mb-0"}>
                    {rupees(ren.balance)}
                  </h4>
                </div>
              </div>
            </div>

            {ren.cleared ? (
              <div class="alert alert-success">
                Renewal paid. Your next renewal is due on {dmy(ren.due_date)}.
              </div>
            ) : ren.due_soon ? (
              <div class="mb-4">
                {ren.overdue && (
                  <div class="alert alert-danger">
                    Your renewal was due on {dmy(ren.due_date)}. Please pay it
                    to keep your center active.
                  </div>
                )}
                <button
                  class="btn btn-Next mb-2"
                  type="button"
                  disabled={paying}
                  onClick={() =>
                    openCheckout(ren.balance, "renewal", "Center Renewal Fee")
                  }
                >
                  Pay Renewal {rupees(ren.balance)}
                </button>
              </div>
            ) : (
              <p class="text-muted">
                Your renewal opens for payment nearer the due date.
              </p>
            )}
          </>
        )}

        <div class="BigTitle mt-4">Payment History</div>
        {fee.payments && fee.payments.length ? (
          <div class="table-responsive mt-2">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Date</th>
                  <th>For</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Transaction ID</th>
                  <th>Status</th>
                  <th>Slip</th>
                </tr>
              </thead>
              <tbody>
                {fee.payments.map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td>{dmy(p.paid_at || p.created_at)}</td>
                    <td class="text-capitalize">
                      {p.fee_type} ({p.payment_type})
                    </td>
                    <td>{rupees(p.amount)}</td>
                    <td>{p.channel_label || p.mode}</td>
                    <td>{p.trx_id || "-"}</td>
                    <td>{p.status}</td>
                    <td>
                      <a
                        class="btn btn-sm btn-comman"
                        href={`${FILE_BASE}center-fee-slip/${p.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p class="mt-2">No payments yet.</p>
        )}
      </div>
    </div>
  );
};

export default Fee;
